/**
 * Column mapping API client — which column in an uploaded file supplies
 * each value the engine reads.
 *
 * Org-scoped like the rest of the app. The registry (our attribute names) comes
 * from the server rather than being listed here on purpose: it is generated
 * from the engine's own field registry, and a copy in the console would drift,
 * leaving the operator offered a field the engine does not read or never asked
 * about one it does.
 */
import api from './client';

const base = (orgUuid) => `/organisations/${orgUuid}/column-mappings`;

export const columnMappingApi = {
    /** What the engine needs, per file — the left-hand side of the editor. */
    getRegistry(orgUuid) {
        return api.get(`${base(orgUuid)}/registry`).then((r) => r.data);
    },

    /** The mapping in force: { version, builtIn, document }. */
    getCurrent(orgUuid) {
        return api.get(base(orgUuid)).then((r) => r.data);
    },

    /** History, newest first. Always includes v1, which is not a stored row. */
    listVersions(orgUuid) {
        return api.get(`${base(orgUuid)}/versions`).then((r) => r.data);
    },

    getVersion(orgUuid, version) {
        return api.get(`${base(orgUuid)}/versions/${version}`).then((r) => r.data);
    },

    /**
     * What an uploaded file actually contains — the right-hand side.
     *
     * @returns { files, readFile, columns, labels }
     */
    getSourceHeaders(orgUuid, year, month, fileType) {
        return api
            .get(`${base(orgUuid)}/source-headers`, { params: { year, month, fileType } })
            .then((r) => r.data);
    },

    /**
     * Read one month's file through a draft mapping and return the first rows
     * as it would read them, plus any problems.
     *
     * The draft is sent whole, not just the changed entries: the server stores
     * a complete document, and sending a partial one would rely on it filling
     * in the rest the same way the editor assumed.
     */
    preview(orgUuid, year, month, fileType, draft) {
        return api
            .post(`${base(orgUuid)}/preview/${year}/${month}/${fileType}`, draft)
            .then((r) => r.data);
    },

    /**
     * Save a new version. The server assigns the number — sending one would be
     * guessing, and it is ignored.
     *
     * @param draft { files: [{ type, columns: [{field, source}], labels: [...] }], note }
     */
    save(orgUuid, draft) {
        return api.post(base(orgUuid), draft).then((r) => r.data);
    },
};

/**
 * Turn a server document into the flat shape the editor edits:
 * `{ [fileType]: { columns: {fieldId: source}, labels: {fieldId: source} } }`.
 *
 * A blank source is preserved as `''` rather than dropped. The server treats an
 * absent entry as "use the default" and a blank one as "deliberately unmapped",
 * and collapsing the two here would silently re-map a link the operator cleared.
 */
export function documentToDraft(document) {
    const draft = {};
    Object.entries(document?.files ?? {}).forEach(([type, section]) => {
        draft[type] = {
            columns: Object.fromEntries(
                (section.columns ?? []).map((e) => [e.field, e.source ?? '']),
            ),
            labels: Object.fromEntries(
                (section.labels ?? []).map((e) => [e.field, e.source ?? '']),
            ),
        };
    });
    return draft;
}

/** The inverse: the editor's flat shape as a save/preview body. */
export function draftToRequest(draft, note) {
    return {
        files: Object.entries(draft).map(([type, section]) => ({
            type,
            columns: Object.entries(section.columns ?? {})
                .map(([field, source]) => ({ field, source })),
            labels: Object.entries(section.labels ?? {})
                .map(([field, source]) => ({ field, source })),
        })),
        note: note?.trim() ? note.trim() : null,
    };
}

/** True when the draft differs from the document it was loaded from. */
export function isDirty(draft, original) {
    return JSON.stringify(draft) !== JSON.stringify(original);
}

/**
 * Rank a file's headings as candidates for one of our fields.
 *
 * Normalised comparison only — case, spaces and punctuation stripped, then an
 * exact match, a containment either way, and finally a token overlap. No
 * fuzzy distance: a near-miss suggestion that happens to be wrong is the one
 * failure this whole feature risks introducing, so a suggestion is offered only
 * when there is a concrete reason, and never applied without a person agreeing.
 */
export function suggestSources(field, headings) {
    const target = normalise(field.label);
    const alt = normalise(field.defaultSource ?? '');
    const targetTokens = tokens(field.label);

    return headings
        .map((heading) => {
            const value = normalise(heading);
            let score = 0;
            if (value && (value === target || (alt && value === alt))) score = 100;
            else if (value && target && (value.includes(target) || target.includes(value))) score = 70;
            else {
                const shared = tokens(heading).filter((tk) => targetTokens.includes(tk));
                if (shared.length) score = 30 + shared.length * 10;
            }
            return { heading, score };
        })
        .filter((c) => c.score > 0)
        .sort((a, b) => b.score - a.score || a.heading.localeCompare(b.heading));
}

const normalise = (s) => (s ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');

const tokens = (s) =>
    (s ?? '')
        .toLowerCase()
        .split(/[^a-z0-9]+/)
        .filter((tk) => tk.length > 2);
